/**
 * Cadastro público — empresa e modalidade (CERES-ACC-005).
 *
 * Chega aqui quem confirmou o e-mail (token de continuação na aba) ou quem
 * retoma com e-mail e senha. Três passos curtos: CNPJ e nome da empresa, a
 * modalidade de uso, e o resultado — que diz a verdade sobre a assinatura:
 * "pronta" só quando há avaliação autorizada; senão, "aguardando ativação".
 *
 * Empresa que já usa o CERES não é vinculada a ninguém por aqui: a tela oferece
 * avisar quem a administra, e o acesso vem por convite.
 *
 * CERES-ACC-005-OPER-001 — modo `usuario`: quem JÁ tem conta funda outra
 * organização pelos mesmos passos, sem token de continuação (a sessão é a
 * prova) e com a senha atual na conclusão. A identidade não muda; a empresa
 * nova aparece na troca de organização de sempre.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SimboloCeres } from '../components/Marca';
import { RodapePublico } from '../components/RodapePublico';
import { ApiError, api } from '../lib/api';
import { t } from '../lib/i18n';
import { useSession } from '../lib/session';
import { formatDate } from '../lib/format';
import {
  esquecerContinuacao,
  guardarContinuacao,
  lerContinuacao,
  preencher,
  type Continuacao,
  type ModalidadeDoPlano,
  type ResultadoDaConclusao,
} from '../lib/cadastro-publico';
import { Field, InlineError, Spinner } from '../components/ui';

type Fase = 'carregando' | 'retomar' | 'empresa' | 'modalidade' | 'resultado';

interface Verificacao {
  cnpj: string;
  jaCadastrada: boolean;
  registro: {
    razaoSocial: string;
    nomeFantasia: string | null;
    situacaoCadastral: string | null;
    municipio: string | null;
    uf: string | null;
  } | null;
}

function motivoDe(erro: unknown): string | null {
  if (!(erro instanceof ApiError)) return null;
  const detalhes = erro.details as { motivo?: unknown } | null | undefined;
  return typeof detalhes?.motivo === 'string' ? detalhes.motivo : null;
}

function precoLegivel(preco: NonNullable<ModalidadeDoPlano['preco']>): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: preco.moeda }).format(
    Number(preco.valorMensalPorUsuarioEmCentavos) / 100,
  );
}

function Passos({ atual }: { atual: 1 | 2 | 3 }): JSX.Element {
  const passos = [
    t('cadastro.passoConta'),
    t('cadastro.passoEmpresa'),
    t('cadastro.passoModalidade'),
  ];
  return (
    <ol className="mb-4 flex justify-center gap-3 text-2xs text-ink-subtle">
      {passos.map((p, i) => (
        <li key={p} className={i + 1 === atual ? 'font-semibold text-brand-600' : undefined}>
          {i + 1}. {p}
        </li>
      ))}
    </ol>
  );
}

/** Resultado da fundação por quem já tem conta (`POST /auth/organizacoes`). */
interface ResultadoDaFundacao extends ResultadoDaConclusao {
  organizacao: { id: string; nome: string };
  jaExistia: boolean;
}

/** Entrar na organização recém-criada: a troca de organização de sempre. */
function EntrarNaNova({ resultado }: { resultado: ResultadoDaFundacao }): JSX.Element {
  const { trocarOrganizacao } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const entrar = async (): Promise<void> => {
    setBusy(true);
    setErro(null);
    try {
      await trocarOrganizacao(resultado.organizacao.id);
      navigate('/', { replace: true });
    } catch (e) {
      setErro(e);
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 flex flex-col items-center gap-2">
      <InlineError error={erro} />
      <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={() => void entrar()}>
        {busy && <Spinner />}
        {preencher(t('outraOrganizacao.entrar'), { empresa: resultado.organizacao.nome })}
      </button>
      <Link to="/" className="text-xs text-ink-muted underline hover:text-ink">
        {t('outraOrganizacao.voltar')}
      </Link>
    </div>
  );
}

export function CadastroDaEmpresaPage({
  modo = 'publico',
}: {
  modo?: 'publico' | 'usuario';
}): JSX.Element {
  const doUsuario = modo === 'usuario';
  const [fase, setFase] = useState<Fase>(doUsuario ? 'empresa' : 'carregando');
  const [continuacao, setContinuacao] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // retomada
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');

  // empresa
  const [cnpj, setCnpj] = useState('');
  const [verificacao, setVerificacao] = useState<Verificacao | null>(null);
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [site, setSite] = useState('');
  const [pedidoDeAcesso, setPedidoDeAcesso] = useState<string | null>(null);

  // modalidade e resultado
  const [modalidades, setModalidades] = useState<ModalidadeDoPlano[] | null>(null);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ResultadoDaConclusao | null>(null);
  // modo usuario: a senha atual, pedida só na conclusão
  const [senhaAtual, setSenhaAtual] = useState('');

  const perderContinuacao = (): void => {
    esquecerContinuacao();
    setContinuacao(null);
    setFase('retomar');
  };

  const tratar = (erro: unknown): void => {
    if (motivoDe(erro) === 'CONTINUACAO_INVALIDA') {
      perderContinuacao();
      return;
    }
    setError(erro);
  };

  useEffect(() => {
    if (doUsuario) return;
    const guardada = lerContinuacao();
    if (guardada === null) {
      setFase('retomar');
      return;
    }
    void (async () => {
      try {
        const situacao = await api.post<{
          concluido: boolean;
          resultado: ResultadoDaConclusao | null;
        }>('/auth/cadastro/situacao', { continuacao: guardada });
        setContinuacao(guardada);
        if (situacao.concluido && situacao.resultado !== null) {
          setResultado(situacao.resultado);
          esquecerContinuacao();
          setFase('resultado');
        } else {
          setFase('empresa');
        }
      } catch {
        perderContinuacao();
      }
    })();
  }, [doUsuario]);

  const executar = async (acao: () => Promise<void>): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await acao();
    } catch (erro) {
      tratar(erro);
    } finally {
      setBusy(false);
    }
  };

  const retomar = (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    return executar(async () => {
      const resposta = await api.post<Continuacao>('/auth/cadastro/retomar', { email, senha });
      guardarContinuacao(resposta.continuacao);
      setContinuacao(resposta.continuacao);
      setFase('empresa');
    });
  };

  const verificar = (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    return executar(async () => {
      const resposta = doUsuario
        ? await api.post<Verificacao>('/auth/organizacoes/verificar', { cnpj })
        : await api.post<Verificacao>('/auth/cadastro/empresa/verificar', { continuacao, cnpj });
      setVerificacao(resposta);
      setPedidoDeAcesso(null);
      setCnpj(resposta.cnpj);
      if (resposta.registro !== null) {
        setRazaoSocial(resposta.registro.razaoSocial);
        setNomeFantasia(resposta.registro.nomeFantasia ?? '');
      }
    });
  };

  const pedirAcesso = (): Promise<void> =>
    executar(async () => {
      const resposta = doUsuario
        ? await api.post<{ mensagem: string }>('/auth/organizacoes/pedir-acesso', { cnpj })
        : await api.post<{ mensagem: string }>('/auth/cadastro/empresa/pedir-acesso', {
            continuacao,
            cnpj,
          });
      setPedidoDeAcesso(resposta.mensagem);
    });

  const carregarModalidades = async (): Promise<void> => {
    const resposta = await api.get<{ data: ModalidadeDoPlano[] }>('/auth/cadastro/modalidades');
    setModalidades(resposta.data);
    setEscolhida((atual) =>
      resposta.data.some((m) => m.codigo === atual) ? atual : (resposta.data[0]?.codigo ?? null),
    );
  };

  const irParaModalidade = (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    return executar(async () => {
      await carregarModalidades();
      setFase('modalidade');
    });
  };

  const concluir = (): Promise<void> =>
    executar(async () => {
      const plano = modalidades?.find((m) => m.codigo === escolhida);
      if (plano === undefined) return;
      try {
        const dados = {
          empresa: {
            cnpj,
            razaoSocial,
            nomeFantasia: nomeFantasia.trim() === '' ? null : nomeFantasia,
            site: site.trim() === '' ? null : site,
          },
          plano: { codigo: plano.codigo, versao: plano.versao },
        };
        const resposta = doUsuario
          ? await api.post<ResultadoDaFundacao>('/auth/organizacoes', { ...dados, senha: senhaAtual })
          : await api.post<ResultadoDaConclusao>('/auth/cadastro/concluir', { continuacao, ...dados });
        if (!doUsuario) esquecerContinuacao();
        setSenhaAtual('');
        setResultado(resposta);
        setFase('resultado');
      } catch (erro) {
        // As condições mudaram enquanto a pessoa escolhia: mostra as novas.
        if (motivoDe(erro) === 'CONDICOES_ALTERADAS') await carregarModalidades();
        throw erro;
      }
    });

  const plano = modalidades?.find((m) => m.codigo === escolhida) ?? null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunken px-4 py-10">
      <div className={`w-full ${fase === 'modalidade' ? 'max-w-2xl' : 'max-w-md'}`}>
        <div className="mb-4 flex flex-col items-center text-center">
          <SimboloCeres className="mb-3 h-11 w-11 text-brand-600" decorativa />
          <h1 className="text-xl font-semibold text-ink">
            {fase === 'retomar'
              ? t('cadastro.retomarTitulo')
              : fase === 'modalidade'
                ? t('cadastro.modalidadeTitulo')
                : fase === 'resultado'
                  ? doUsuario && (resultado as ResultadoDaFundacao | null)?.jaExistia === true
                    ? t('outraOrganizacao.titulo')
                    : resultado?.assinatura.situacao === 'PENDING'
                      ? t('cadastro.pendenteTitulo')
                      : t('cadastro.prontaTitulo')
                  : doUsuario
                    ? t('outraOrganizacao.titulo')
                    : t('cadastro.empresaTitulo')}
          </h1>
          {doUsuario && fase === 'empresa' && (
            <p className="mt-1 max-w-md text-sm text-ink-muted">{t('outraOrganizacao.texto')}</p>
          )}
        </div>

        {!doUsuario && (fase === 'empresa' || fase === 'modalidade') && (
          <Passos atual={fase === 'empresa' ? 2 : 3} />
        )}

        <div className="panel px-5 py-5">
          {fase === 'carregando' && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}

          {fase === 'retomar' && (
            <form onSubmit={(event) => void retomar(event)} className="space-y-4" noValidate>
              <p className="text-sm text-ink-muted">{t('cadastro.retomarTexto')}</p>
              <Field label={t('cadastro.email')} htmlFor="email" required>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="field-input"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>
              <Field label={t('cadastro.senha')} htmlFor="senha" required>
                <input
                  id="senha"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="field-input"
                  value={senha}
                  onChange={(event) => setSenha(event.target.value)}
                />
              </Field>
              <InlineError error={error} />
              <button type="submit" className="btn-primary w-full" disabled={busy}>
                {busy && <Spinner />}
                {t('cadastro.retomarBotao')}
              </button>
            </form>
          )}

          {fase === 'empresa' && (
            <div className="space-y-4">
              <p className="text-sm text-ink-muted">{t('cadastro.empresaTexto')}</p>
              <form
                onSubmit={(event) => void verificar(event)}
                className="flex items-end gap-2"
                noValidate
              >
                <div className="flex-1">
                  <Field label={t('cadastro.cnpj')} htmlFor="cnpj" required>
                    <input
                      id="cnpj"
                      type="text"
                      inputMode="numeric"
                      required
                      autoFocus
                      placeholder="00.000.000/0000-00"
                      className="field-input"
                      value={cnpj}
                      onChange={(event) => {
                        setCnpj(event.target.value);
                        setVerificacao(null);
                      }}
                    />
                  </Field>
                </div>
                <button
                  type="submit"
                  className="btn-secondary"
                  disabled={busy || cnpj.trim() === ''}
                >
                  {busy && verificacao === null && <Spinner />}
                  {t('cadastro.verificarCnpj')}
                </button>
              </form>

              {verificacao?.jaCadastrada === true && (
                <div
                  role="status"
                  className="rounded border border-line bg-surface-sunken p-3 text-sm"
                >
                  <p className="font-medium text-ink">{t('cadastro.empresaExistenteTitulo')}</p>
                  <p className="mt-1 text-ink-muted">{t('cadastro.empresaExistenteTexto')}</p>
                  {pedidoDeAcesso !== null ? (
                    <p className="mt-2 text-ink">{pedidoDeAcesso}</p>
                  ) : (
                    <button
                      type="button"
                      className="btn-secondary btn-sm mt-3"
                      disabled={busy}
                      onClick={() => void pedirAcesso()}
                    >
                      {t('cadastro.pedirAcesso')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="ml-2 mt-3 text-xs underline text-ink-muted hover:text-ink"
                    onClick={() => {
                      setVerificacao(null);
                      setCnpj('');
                    }}
                  >
                    {t('cadastro.outroCnpj')}
                  </button>
                </div>
              )}

              {verificacao?.jaCadastrada === false && (
                <form
                  onSubmit={(event) => void irParaModalidade(event)}
                  className="space-y-4"
                  noValidate
                >
                  <p className="text-xs text-ink-muted">
                    {verificacao.registro !== null
                      ? t('cadastro.consultaPreencheu')
                      : t('cadastro.consultaIndisponivel')}
                    {verificacao.registro?.situacaoCadastral != null && (
                      <>
                        {' '}
                        {t('cadastro.situacaoCadastral')} {verificacao.registro.situacaoCadastral}
                      </>
                    )}
                  </p>
                  <Field label={t('cadastro.razaoSocial')} htmlFor="razao" required>
                    <input
                      id="razao"
                      type="text"
                      autoComplete="organization"
                      required
                      className="field-input"
                      value={razaoSocial}
                      onChange={(event) => setRazaoSocial(event.target.value)}
                    />
                  </Field>
                  <Field label={t('cadastro.nomeFantasia')} htmlFor="fantasia">
                    <input
                      id="fantasia"
                      type="text"
                      className="field-input"
                      value={nomeFantasia}
                      onChange={(event) => setNomeFantasia(event.target.value)}
                    />
                  </Field>
                  <Field label={t('cadastro.site')} htmlFor="site">
                    <input
                      id="site"
                      type="text"
                      inputMode="url"
                      autoComplete="url"
                      placeholder="www.suaempresa.com.br"
                      className="field-input"
                      value={site}
                      onChange={(event) => setSite(event.target.value)}
                    />
                  </Field>
                  <button
                    type="submit"
                    className="btn-primary w-full"
                    disabled={busy || razaoSocial.trim().length < 2}
                  >
                    {busy && <Spinner />}
                    {t('cadastro.continuar')}
                  </button>
                </form>
              )}
              <InlineError error={error} />
            </div>
          )}

          {fase === 'modalidade' && modalidades !== null && (
            <div className="space-y-4">
              <p className="text-sm text-ink-muted">{t('cadastro.modalidadeTexto')}</p>
              {modalidades.length === 0 ? (
                <p role="status" className="text-sm text-ink">
                  {t('cadastro.semModalidades')}
                </p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-3" role="radiogroup">
                  {modalidades.map((m) => {
                    const ativa = m.codigo === escolhida;
                    return (
                      <button
                        key={m.codigo}
                        type="button"
                        role="radio"
                        aria-checked={ativa}
                        onClick={() => setEscolhida(m.codigo)}
                        className={`rounded border p-3 text-left text-xs ${
                          ativa
                            ? 'border-brand-600 ring-1 ring-brand-600'
                            : 'border-line hover:border-ink-subtle'
                        }`}
                      >
                        <span className="block text-sm font-semibold text-ink">{m.nome}</span>
                        {m.descricao !== null && (
                          <span className="mt-1 block text-ink-muted">{m.descricao}</span>
                        )}
                        <span className="mt-2 block text-ink">
                          {m.modalidade.tipo === 'AVALIACAO_GRATUITA'
                            ? preencher(t('cadastro.modalidadeAvaliacao'), {
                                dias: m.modalidade.dias,
                                usuarios: m.modalidade.limiteDeUsuarios,
                              })
                            : t('cadastro.modalidadeAtivacao')}
                        </span>
                        <span className="mt-2 block text-ink-muted">
                          {m.preco === null
                            ? t('cadastro.precoNaoPublicado')
                            : `${t('cadastro.preco')} ${precoLegivel(m.preco)}`}
                        </span>
                        <span className="mt-1 block text-ink-muted">
                          {t('cadastro.limiteUsuarios')}{' '}
                          {m.limiteDeUsuarios ?? t('cadastro.semLimitePublicado')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {plano !== null && plano.funcionalidades.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium text-brand-600">
                    {t('cadastro.funcionalidades')} — {plano.nome} ({plano.funcionalidades.length})
                  </summary>
                  <ul className="mt-2 grid gap-1 sm:grid-cols-2">
                    {plano.funcionalidades.map((f) => (
                      <li key={f} className="text-ink">
                        {f}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {doUsuario && (
                <Field
                  label={t('outraOrganizacao.senha')}
                  htmlFor="senha-atual"
                  hint={t('outraOrganizacao.senhaAjuda')}
                  required
                >
                  <input
                    id="senha-atual"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="field-input"
                    value={senhaAtual}
                    onChange={(event) => setSenhaAtual(event.target.value)}
                  />
                </Field>
              )}
              <InlineError error={error} />
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy}
                  onClick={() => setFase('empresa')}
                >
                  {t('cadastro.voltar')}
                </button>
                <button
                  type="button"
                  className="btn-primary flex-1"
                  disabled={busy || plano === null || (doUsuario && senhaAtual === '')}
                  onClick={() => void concluir()}
                >
                  {busy && <Spinner />}
                  {doUsuario ? t('outraOrganizacao.criar') : t('cadastro.concluir')}
                </button>
              </div>
            </div>
          )}

          {fase === 'resultado' && resultado !== null && doUsuario && (
            <div className="text-center">
              <p role="status" className="text-sm text-ink-muted">
                {(resultado as ResultadoDaFundacao).jaExistia
                  ? preencher(t('outraOrganizacao.jaExistia'), { empresa: resultado.organizacao.nome })
                  : resultado.assinatura.situacao === 'PENDING'
                    ? preencher(t('outraOrganizacao.pendenteTexto'), {
                        empresa: resultado.organizacao.nome,
                      })
                    : preencher(t('outraOrganizacao.prontaTexto'), {
                        empresa: resultado.organizacao.nome,
                        data: formatDate(resultado.assinatura.avaliacaoAte),
                      })}
              </p>
              <EntrarNaNova resultado={resultado as ResultadoDaFundacao} />
            </div>
          )}

          {fase === 'resultado' && resultado !== null && !doUsuario && (
            <div className="text-center">
              <p role="status" className="text-sm text-ink-muted">
                {resultado.assinatura.situacao === 'PENDING'
                  ? preencher(t('cadastro.pendenteTexto'), { empresa: resultado.organizacao.nome })
                  : preencher(t('cadastro.prontaAvaliacao'), {
                      empresa: resultado.organizacao.nome,
                      data: formatDate(resultado.assinatura.avaliacaoAte),
                    })}
              </p>
              <Link to="/entrar" className="btn-primary btn-sm mt-4 inline-flex">
                {t('cadastro.entrarAgora')}
              </Link>
            </div>
          )}
        </div>

        {!doUsuario && <RodapePublico />}
      </div>
    </div>
  );
}
